import { defineStore } from "pinia";
import { ref } from "vue";
import type {
  PaymentTariffsResponse,
  PaymentTariff,
  PaymentMethod,
  PaymentMethodsResponse,
  GetPaymentMethodsRequest,
  CreateBillRequest,
  CreateBillResponse,
  BillStatusResponse,
  BillsHistoryResponse,
  BillHistoryItem,
} from "./types";
import {
  getPrice,
  getPaymentMethods,
  createBill,
  checkBillStatus,
  getBillsHistory,
} from "../api/paymentRepositoy";
import { authorize } from "@/shared/lib/auth/authorize";
import { PostLogout } from "@/shared/repositories/LogoutRepository";
import { SentryService } from "@/shared/lib/sentry/sentryService";

const HOME_MIN_PRICE_CACHE_KEY = "vpn_home_min_price_cache";
const HOME_MIN_PRICE_TTL = 60 * 1000;

interface HomeMinPriceCacheSnapshot {
  homeMinPrice: number | null;
  homeMinPriceFetchedAt: number;
}

function readHomeMinPriceCache(): HomeMinPriceCacheSnapshot | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawCache = window.sessionStorage.getItem(HOME_MIN_PRICE_CACHE_KEY);

    if (!rawCache) {
      return null;
    }

    const parsedCache = JSON.parse(rawCache) as Partial<HomeMinPriceCacheSnapshot>;

    return {
      homeMinPrice:
        typeof parsedCache.homeMinPrice === "number"
          ? parsedCache.homeMinPrice
          : null,
      homeMinPriceFetchedAt:
        typeof parsedCache.homeMinPriceFetchedAt === "number"
          ? parsedCache.homeMinPriceFetchedAt
          : 0,
    };
  } catch (error) {
    console.error("Не удалось прочитать кэш минимальной цены:", error);
    return null;
  }
}

function writeHomeMinPriceCache(snapshot: HomeMinPriceCacheSnapshot) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      HOME_MIN_PRICE_CACHE_KEY,
      JSON.stringify(snapshot),
    );
  } catch (error) {
    console.error("Не удалось сохранить кэш минимальной цены:", error);
  }
}

function removeHomeMinPriceCache() {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(HOME_MIN_PRICE_CACHE_KEY);
}

export const usePaymentStore = defineStore("payment", () => {
  const homeMinPriceCache = readHomeMinPriceCache();

  const tariffs = ref<PaymentTariff[]>([]);
  const selectedTariff = ref<PaymentTariff | null>(null);
  const paymentMethods = ref<PaymentMethod[]>([]);
  const selectedPaymentMethod = ref<PaymentMethod | null>(null);
  const isLoading = ref(false);
  const isLoadingMethods = ref(false);
  const isCreatingInvoice = ref(false);
  const isCheckingPayment = ref(false);
  const currentBillId = ref<string | null>(null);
  const lastFailedBillRequest = ref<CreateBillRequest | null>(null);
  const error = ref<string | null>(null);
  const errorStatus = ref<string | null>(null);

  // История транзакций
  const billsHistory = ref<BillHistoryItem[]>([]);
  const autopayment = ref<boolean>(false);
  const nextAutoPayDate = ref<string>("");
  const autopayAmount = ref<number>(0);
  const isLoadingHistory = ref(false);
  const lastHistoryFetchTime = ref<number>(0); // Время последней загрузки истории
  const homeMinPrice = ref<number | null>(homeMinPriceCache?.homeMinPrice ?? null);
  const homeMinPriceFetchedAt = ref<number>(
    homeMinPriceCache?.homeMinPriceFetchedAt ?? 0,
  );
  const isLoadingHomeMinPrice = ref(false);

  // Переменная для хранения таймера проверки статуса
  let paymentStatusTimer: number | null = null;

  function syncHomeMinPriceCache() {
    writeHomeMinPriceCache({
      homeMinPrice: homeMinPrice.value,
      homeMinPriceFetchedAt: homeMinPriceFetchedAt.value,
    });
  }

  function clearHomeMinPrice() {
    homeMinPrice.value = null;
    homeMinPriceFetchedAt.value = 0;
    removeHomeMinPriceCache();
  }

  async function fetchHomeMinPrice(force: boolean = false) {
    const now = Date.now();

    if (isLoadingHomeMinPrice.value) {
      console.log("Запрос минимальной цены для главной уже выполняется, пропускаем");
      return;
    }

    if (
      !force &&
      homeMinPriceFetchedAt.value > 0 &&
      now - homeMinPriceFetchedAt.value < HOME_MIN_PRICE_TTL
    ) {
      console.log("Кэш минимальной цены для главной еще свежий, сетевой запрос не нужен");
      return;
    }

    try {
      isLoadingHomeMinPrice.value = true;

      const response = await getPrice(undefined, true);
      const validPerMonthPrices = response.data
        .map((tariff) => Number(tariff.per_month))
        .filter((perMonth) => Number.isFinite(perMonth) && perMonth > 0);

      homeMinPrice.value = validPerMonthPrices.length ? Math.min(...validPerMonthPrices) : null;
      homeMinPriceFetchedAt.value = Date.now();
      syncHomeMinPriceCache();
    } catch (error) {
      console.error("Ошибка загрузки минимальной цены для главной:", error);
    } finally {
      isLoadingHomeMinPrice.value = false;
    }
  }

  async function fetchTariffs(
    subscriptionID?: string | null,
    autopaymentEnabled: boolean = false,
  ) {
    // Если запрос уже выполняется, не запускаем новый
    if (isLoading.value) {
      console.log("Запрос тарифов уже выполняется, пропускаем");
      return;
    }

    try {
      isLoading.value = true;
      error.value = null;

      // Добавляем breadcrumb для отслеживания
      SentryService.addBreadcrumb("Загрузка тарифов", "payment", "info");

      const response = await getPrice(subscriptionID, autopaymentEnabled);

      console.log("Payment API response:", response.data);

      // Обрабатываем ответ с сервера
      if (response.data && Array.isArray(response.data)) {
        tariffs.value = response.data;
        console.log("Set tariffs from response:", response.data);

        // Устанавливаем контекст для успешной загрузки тарифов
        SentryService.setContext("tariffs_loaded", {
          subscription_id: subscriptionID,
          autopayment_enabled: autopaymentEnabled,
          tariffs_count: response.data.length,
          tariffs: response.data.map((tariff) => ({
            id: tariff.id,
            name: tariff.name,
            months: tariff.months,
            price: tariff.price,
            per_month: tariff.per_month,
          })),
        });

        SentryService.captureMessage("Тарифы успешно загружены", "info");
        SentryService.addBreadcrumb(
          `Загружено ${response.data.length} тарифов`,
          "payment",
          "info",
        );
      }
    } catch (err: any) {
      error.value = err instanceof Error ? err.message : "Неизвестная ошибка";
      tariffs.value = [];

      // Устанавливаем контекст для ошибки загрузки тарифов
      SentryService.setContext("tariffs_load_error", {
        subscription_id: subscriptionID,
        autopayment_enabled: autopaymentEnabled,
        error_message: error.value,
        error_status: err.status || "unknown",
        error_type: err instanceof Error ? err.constructor.name : typeof err,
      });

      // Отправляем ошибку в Sentry
      SentryService.captureException(err, {
        context: "fetchTariffs",
        subscriptionID,
        autopaymentEnabled,
        errorMessage: error.value,
      });

      if (err.status === 401) {
        console.log("401==========");
        errorStatus.value = err.status;
        await PostLogout();
        await authorize();
      }
    } finally {
      isLoading.value = false;
    }
  }

  function selectTariff(tariff: PaymentTariff) {
    selectedTariff.value = tariff;

    SentryService.addBreadcrumb(
      `Выбран тариф: ${tariff.name}`,
      "payment",
      "info",
    );
  }

  async function fetchPaymentMethods(params: GetPaymentMethodsRequest) {
    try {
      isLoadingMethods.value = true;
      error.value = null;

      SentryService.addBreadcrumb("Загрузка методов оплаты", "payment", "info");

      const response = await getPaymentMethods(params);

      console.log("Payment Methods API response:", response.data);

      // Обрабатываем ответ с сервера
      if (
        response.data &&
        response.data.methods &&
        Array.isArray(response.data.methods)
      ) {
        paymentMethods.value = response.data.methods;
        console.log(
          "Set payment methods from response:",
          response.data.methods,
        );

        // Автоматически выбираем приоритетный метод оплаты, если он есть
        const priorityMethod = response.data.methods.find(
          (method) => method.is_priority && method.is_active,
        );
        if (priorityMethod) {
          selectedPaymentMethod.value = priorityMethod;
        }

        // Устанавливаем контекст для успешной загрузки методов оплаты
        SentryService.setContext("payment_methods_loaded", {
          months: params.months,
          autopayment_enabled: params.autopayment_enabled,
          subscription_id: params.subscription_id ?? null,
          methods_count: response.data.methods.length,
          priority_method: priorityMethod
            ? {
                id: priorityMethod.id,
                name: priorityMethod.name,
                method_code: priorityMethod.method_code,
              }
            : null,
          methods: response.data.methods.map((method) => ({
            id: method.id,
            name: method.name,
            method_code: method.method_code,
            is_priority: method.is_priority,
            is_active: method.is_active,
          })),
        });

        SentryService.captureMessage("Методы оплаты успешно загружены", "info");
        SentryService.addBreadcrumb(
          `Загружено ${response.data.methods.length} методов оплаты`,
          "payment",
          "info",
        );
      }
    } catch (err: any) {
      error.value =
        err instanceof Error
          ? err.message
          : "Неизвестная ошибка при загрузке методов оплаты";
      paymentMethods.value = [];

      // Устанавливаем контекст для ошибки загрузки методов оплаты
      SentryService.setContext("payment_methods_load_error", {
        months: params.months,
        autopayment_enabled: params.autopayment_enabled,
        subscription_id: params.subscription_id ?? null,
        error_message: error.value,
        error_status: err.status || "unknown",
        error_type: err instanceof Error ? err.constructor.name : typeof err,
      });

      // Отправляем ошибку в Sentry
      SentryService.captureException(err, {
        context: "fetchPaymentMethods",
        params,
        errorMessage: error.value,
      });

      if (err.status === 401) {
        console.log("401 - Payment Methods ==========");
        errorStatus.value = err.status;
        await PostLogout();
        await authorize();
      }
    } finally {
      isLoadingMethods.value = false;
    }
  }

  function selectPaymentMethod(method: PaymentMethod) {
    selectedPaymentMethod.value = method;

    SentryService.addBreadcrumb(
      `Выбран метод оплаты: ${method.name}`,
      "payment",
      "info",
    );
  }

  function clearTariffs() {
    tariffs.value = [];
    selectedTariff.value = null;
    error.value = null;
  }

  function clearPaymentMethods() {
    paymentMethods.value = [];
    selectedPaymentMethod.value = null;
    currentBillId.value = null;
  }

  function clearAll() {
    stopPaymentStatusPolling(); // Останавливаем проверку статуса
    clearTariffs();
    clearPaymentMethods();
    clearBillsHistory();
    clearHomeMinPrice();
  }

  function clearBillsHistory() {
    billsHistory.value = [];
    autopayment.value = false;
    nextAutoPayDate.value = "";
    autopayAmount.value = 0;
    lastHistoryFetchTime.value = 0;
  }

  async function fetchBillsHistory(forceRefresh: boolean = false) {
    // Если запрос уже выполняется, не запускаем новый
    if (isLoadingHistory.value) {
      console.log(
        "⚠️ fetchBillsHistory: Запрос истории транзакций уже выполняется, пропускаем",
      );
      return;
    }

    // Проверяем, не загружали ли данные недавно (меньше 5 минут назад)
    const currentTime = Date.now();
    const timeSinceLastFetch = currentTime - lastHistoryFetchTime.value;
    const fiveMinutes = 5 * 60 * 1000; // 5 минут в миллисекундах

    if (
      !forceRefresh &&
      timeSinceLastFetch < fiveMinutes &&
      billsHistory.value.length > 0
    ) {
      console.log(
        `⏱️ fetchBillsHistory: Данные загружены недавно (${Math.round(timeSinceLastFetch / 1000)}с назад), пропускаем`,
      );
      return;
    }

    const callId = Date.now();
    console.log(
      `🚀 fetchBillsHistory: Начинаем загрузку истории транзакций [${callId}]`,
    );

    try {
      isLoadingHistory.value = true;
      error.value = null;

      console.log(
        `📡 fetchBillsHistory: Отправляем запрос на сервер [${callId}]`,
      );
      SentryService.addBreadcrumb(
        "Загрузка истории транзакций",
        "payment",
        "info",
      );

      const response = await getBillsHistory();

      console.log(
        `✅ fetchBillsHistory: Получен ответ от сервера [${callId}]:`,
        response.data,
      );

      // Обрабатываем ответ с сервера
      if (response.data) {
        billsHistory.value = response.data.history || [];
        autopayment.value = response.data.autopayment || false;
        nextAutoPayDate.value = response.data.next_auto_pay_date || "";
        autopayAmount.value = response.data.autopay_amount || 0;
        lastHistoryFetchTime.value = currentTime; // Обновляем время последней загрузки

        console.log(
          `💾 fetchBillsHistory: Данные сохранены в store [${callId}]:`,
          {
            historyCount: billsHistory.value.length,
            autopayment: autopayment.value,
            nextAutoPayDate: nextAutoPayDate.value,
            autopayAmount: autopayAmount.value,
          },
        );

        // Устанавливаем контекст для успешной загрузки истории
        SentryService.setContext("bills_history_loaded", {
          history_count: billsHistory.value.length,
          autopayment: autopayment.value,
          next_auto_pay_date: nextAutoPayDate.value,
          autopay_amount: autopayAmount.value,
        });

        SentryService.captureMessage(
          "История транзакций успешно загружена",
          "info",
        );
        SentryService.addBreadcrumb(
          `Загружено ${billsHistory.value.length} транзакций`,
          "payment",
          "info",
        );
      }
    } catch (err: any) {
      console.error(
        `❌ fetchBillsHistory: Ошибка при загрузке истории транзакций [${callId}]:`,
        err,
      );

      error.value =
        err instanceof Error
          ? err.message
          : "Неизвестная ошибка при загрузке истории транзакций";
      billsHistory.value = [];
      autopayment.value = false;
      nextAutoPayDate.value = "";
      autopayAmount.value = 0;

      // Устанавливаем контекст для ошибки загрузки истории
      SentryService.setContext("bills_history_load_error", {
        error_message: error.value,
        error_status: err.status || "unknown",
        error_type: err instanceof Error ? err.constructor.name : typeof err,
      });

      // Отправляем ошибку в Sentry
      SentryService.captureException(err, {
        context: "fetchBillsHistory",
        errorMessage: error.value,
      });

      if (err.status === 401) {
        console.log(
          `🔐 fetchBillsHistory: Ошибка 401 - требуется повторная авторизация [${callId}]`,
        );
        errorStatus.value = err.status;
        // НЕ ВЫЗЫВАЕМ повторно authorize здесь, чтобы избежать циклов
        // await PostLogout()
        // await authorize()
      }
    } finally {
      isLoadingHistory.value = false;
      console.log(`🏁 fetchBillsHistory: Завершаем загрузку [${callId}]`);
    }
  }

  async function createPaymentBill(
    billData: CreateBillRequest,
  ): Promise<CreateBillResponse> {
    try {
      isCreatingInvoice.value = true;
      error.value = null;

      console.log("💳 PaymentStore: createPaymentBill called with:", billData);

      // Проверяем, что передается для продления
      if (billData.subscription_id) {
        console.log(
          "🔄 ПРОДЛЕНИЕ: subscription_id =",
          billData.subscription_id,
        );
      }

      // Проверяем, что передается для новой подписки
      if (billData.server) {
        console.log("🆕 НОВАЯ: server =", billData.server);
      }

      SentryService.addBreadcrumb(
        "Создание счета на оплату",
        "payment",
        "info",
      );

      const response = await createBill(billData);

      console.log("💳 Create Bill API response:", response.data);
      lastFailedBillRequest.value = null;

      // Сохраняем bill_id для последующей проверки статуса
      if ("bill_id" in response.data) {
        currentBillId.value = response.data.bill_id ?? null;

        // Устанавливаем контекст для успешного создания счета
        SentryService.setContext("bill_created", {
          bill_id: response.data.bill_id,
          tariff: selectedTariff.value
            ? {
                id: selectedTariff.value.id,
                name: selectedTariff.value.name,
                months: selectedTariff.value.months,
                price: selectedTariff.value.price,
              }
            : null,
          payment_method: selectedPaymentMethod.value
            ? {
                id: selectedPaymentMethod.value.id,
                name: selectedPaymentMethod.value.name,
                method_code: selectedPaymentMethod.value.method_code,
              }
            : null,
          request_data: billData,
        });

        SentryService.captureMessage("Счет на оплату создан успешно", "info");
        SentryService.addBreadcrumb(
          `Счет создан успешно: ${response.data.bill_id}`,
          "payment",
          "info",
        );
      }

      return response.data;
    } catch (err: any) {
      error.value =
        err instanceof Error
          ? err.message
          : "Неизвестная ошибка при создании счета";
      lastFailedBillRequest.value = { ...billData };

      // Устанавливаем контекст для ошибки создания счета
      SentryService.setContext("bill_creation_error", {
        error_message: error.value,
        error_status: err.status || "unknown",
        error_type: err instanceof Error ? err.constructor.name : typeof err,
        tariff: selectedTariff.value
          ? {
              id: selectedTariff.value.id,
              name: selectedTariff.value.name,
              months: selectedTariff.value.months,
              price: selectedTariff.value.price,
            }
          : null,
        payment_method: selectedPaymentMethod.value
          ? {
              id: selectedPaymentMethod.value.id,
              name: selectedPaymentMethod.value.name,
              method_code: selectedPaymentMethod.value.method_code,
            }
          : null,
        request_data: billData,
      });

      // Отправляем ошибку в Sentry
      SentryService.captureException(err, {
        context: "createPaymentBill",
        billData,
        errorMessage: error.value,
      });

      if (err.status === 401) {
        console.log("401 - Create Bill ==========");
        errorStatus.value = err.status;
        await PostLogout();
        await authorize();
      }

      throw err;
    } finally {
      isCreatingInvoice.value = false;
    }
  }

  async function checkPaymentStatus(
    billId?: string,
  ): Promise<BillStatusResponse> {
    const idToCheck = billId || currentBillId.value;

    if (!idToCheck) {
      const noIdError = new Error("Bill ID не найден");

      SentryService.captureException(noIdError, {
        context: "checkPaymentStatus",
        billId,
        currentBillId: currentBillId.value,
      });

      throw noIdError;
    }

    try {
      isCheckingPayment.value = true;
      error.value = null;

      const response = await checkBillStatus(idToCheck);

      console.log("Check Bill Status API response:", response.data);

      // Устанавливаем контекст для проверки статуса
      SentryService.setContext("payment_status_check", {
        bill_id: idToCheck,
        status_result: response.data,
        tariff: selectedTariff.value
          ? {
              id: selectedTariff.value.id,
              name: selectedTariff.value.name,
              months: selectedTariff.value.months,
              price: selectedTariff.value.price,
            }
          : null,
        payment_method: selectedPaymentMethod.value
          ? {
              id: selectedPaymentMethod.value.id,
              name: selectedPaymentMethod.value.name,
              method_code: selectedPaymentMethod.value.method_code,
            }
          : null,
      });

      SentryService.addBreadcrumb(
        `Проверка статуса оплаты: ${idToCheck}`,
        "payment",
        "info",
      );

      return response.data;
    } catch (err: any) {
      error.value =
        err instanceof Error
          ? err.message
          : "Неизвестная ошибка при проверке статуса оплаты";

      // Устанавливаем контекст для ошибки проверки статуса
      SentryService.setContext("payment_status_check_error_single", {
        bill_id: idToCheck,
        error_message: error.value,
        error_status: err.status || "unknown",
        error_type: err instanceof Error ? err.constructor.name : typeof err,
        tariff: selectedTariff.value
          ? {
              id: selectedTariff.value.id,
              name: selectedTariff.value.name,
              months: selectedTariff.value.months,
              price: selectedTariff.value.price,
            }
          : null,
        payment_method: selectedPaymentMethod.value
          ? {
              id: selectedPaymentMethod.value.id,
              name: selectedPaymentMethod.value.name,
              method_code: selectedPaymentMethod.value.method_code,
            }
          : null,
      });

      // Отправляем ошибку в Sentry
      SentryService.captureException(err, {
        context: "checkPaymentStatus",
        billId: idToCheck,
        errorMessage: error.value,
      });

      if (err.status === 401) {
        console.log("401 - Check Bill Status ==========");
        errorStatus.value = err.status;
        await PostLogout();
        await authorize();
      }

      if (err.status === 403 || err.status === 404) {
        errorStatus.value = String(err.status);
      }

      throw err;
    } finally {
      isCheckingPayment.value = false;
    }
  }

  // Метод для остановки проверки статуса оплаты
  function stopPaymentStatusPolling() {
    if (paymentStatusTimer !== null) {
      clearTimeout(paymentStatusTimer);
      paymentStatusTimer = null;
      isCheckingPayment.value = false;

      // Устанавливаем контекст для остановки проверки
      SentryService.setContext("payment_polling_stopped", {
        bill_id: currentBillId.value,
        reason: "manual_stop",
        tariff: selectedTariff.value
          ? {
              id: selectedTariff.value.id,
              name: selectedTariff.value.name,
              months: selectedTariff.value.months,
              price: selectedTariff.value.price,
            }
          : null,
        payment_method: selectedPaymentMethod.value
          ? {
              id: selectedPaymentMethod.value.id,
              name: selectedPaymentMethod.value.name,
              method_code: selectedPaymentMethod.value.method_code,
            }
          : null,
      });

      SentryService.captureMessage(
        "Проверка статуса оплаты остановлена пользователем",
        "info",
      );
      SentryService.addBreadcrumb(
        "Проверка статуса оплаты остановлена",
        "payment",
        "info",
      );

      console.log("Проверка статуса оплаты остановлена");
    }
  }

  async function retryLastFailedBill(): Promise<CreateBillResponse> {
    if (!lastFailedBillRequest.value) {
      throw new Error("Нет данных для повторного создания счёта");
    }

    return createPaymentBill({ ...lastFailedBillRequest.value });
  }

  // Метод для периодической проверки статуса оплаты
  function startPaymentStatusPolling(
    billId: string,
    onSuccess: () => void,
    onError: (error: string) => void,
    interval: number = 3000,
    maxAttempts: number = 40, // 2 минуты при интервале 3 секунды
  ) {
    // Останавливаем предыдущую проверку, если она была
    stopPaymentStatusPolling();

    let attempts = 0;

    // Устанавливаем контекст для начала мониторинга
    SentryService.setContext("payment_polling_started", {
      bill_id: billId,
      interval_ms: interval,
      max_attempts: maxAttempts,
      max_wait_time: `${maxAttempts * interval}ms`,
      tariff: selectedTariff.value
        ? {
            id: selectedTariff.value.id,
            name: selectedTariff.value.name,
            months: selectedTariff.value.months,
            price: selectedTariff.value.price,
          }
        : null,
      payment_method: selectedPaymentMethod.value
        ? {
            id: selectedPaymentMethod.value.id,
            name: selectedPaymentMethod.value.name,
            method_code: selectedPaymentMethod.value.method_code,
          }
        : null,
    });

    SentryService.captureMessage("Начат мониторинг статуса оплаты", "info");
    SentryService.addBreadcrumb(
      `Начат мониторинг статуса оплаты: ${billId}`,
      "payment",
      "info",
    );

    const checkStatus = async () => {
      // Проверяем, не была ли остановлена проверка
      if (paymentStatusTimer === null) {
        console.log("Проверка статуса была отменена");
        return;
      }

      attempts++;

      try {
        const result = await checkPaymentStatus(billId);

        if (result.paid) {
          // Отправляем успешную оплату в Sentry как информационное событие
          SentryService.setContext("payment_success", {
            bill_id: billId,
            attempts_count: attempts,
            total_wait_time: `${attempts * interval}ms`,
            tariff: selectedTariff.value
              ? {
                  id: selectedTariff.value.id,
                  name: selectedTariff.value.name,
                  months: selectedTariff.value.months,
                  price: selectedTariff.value.price,
                }
              : null,
            payment_method: selectedPaymentMethod.value
              ? {
                  id: selectedPaymentMethod.value.id,
                  name: selectedPaymentMethod.value.name,
                  method_code: selectedPaymentMethod.value.method_code,
                }
              : null,
          });

          SentryService.captureMessage("Оплата успешно завершена", "info");

          SentryService.addBreadcrumb(
            `Оплата успешно завершена: ${billId}`,
            "payment",
            "info",
          );

          stopPaymentStatusPolling(); // Останавливаем проверку
          onSuccess();
          return;
        }

        if (attempts >= maxAttempts) {
          const timeoutError = new Error("Превышено время ожидания оплаты");

          // Устанавливаем контекст для Sentry
          SentryService.setContext("payment_timeout", {
            bill_id: billId,
            attempts_count: attempts,
            max_attempts: maxAttempts,
            interval_ms: interval,
            total_wait_time: `${attempts * interval}ms`,
            tariff: selectedTariff.value
              ? {
                  id: selectedTariff.value.id,
                  name: selectedTariff.value.name,
                  months: selectedTariff.value.months,
                  price: selectedTariff.value.price,
                }
              : null,
            payment_method: selectedPaymentMethod.value
              ? {
                  id: selectedPaymentMethod.value.id,
                  name: selectedPaymentMethod.value.name,
                  method_code: selectedPaymentMethod.value.method_code,
                }
              : null,
          });

          // Отправляем ошибку таймаута в Sentry
          SentryService.captureException(timeoutError, {
            context: "startPaymentStatusPolling",
            bill_id: billId,
            attempts,
            maxAttempts,
            interval,
            totalWaitTime: `${attempts * interval}ms`,
          });

          stopPaymentStatusPolling(); // Останавливаем проверку
          onError("Превышено время ожидания оплаты");
          return;
        }

        // Продолжаем проверку, если не было остановки
        if (paymentStatusTimer !== null) {
          paymentStatusTimer = setTimeout(
            checkStatus,
            interval,
          ) as unknown as number;
        }
      } catch (error) {
        const pollingError = new Error("Ошибка проверки статуса оплаты");

        // Устанавливаем контекст для Sentry
        SentryService.setContext("payment_status_check_error", {
          bill_id: billId,
          attempts_count: attempts,
          max_attempts: maxAttempts,
          interval_ms: interval,
          total_elapsed_time: `${attempts * interval}ms`,
          error_message:
            error instanceof Error ? error.message : "Unknown error",
          error_type:
            error instanceof Error ? error.constructor.name : typeof error,
          tariff: selectedTariff.value
            ? {
                id: selectedTariff.value.id,
                name: selectedTariff.value.name,
                months: selectedTariff.value.months,
                price: selectedTariff.value.price,
              }
            : null,
          payment_method: selectedPaymentMethod.value
            ? {
                id: selectedPaymentMethod.value.id,
                name: selectedPaymentMethod.value.name,
                method_code: selectedPaymentMethod.value.method_code,
              }
            : null,
        });

        // Отправляем ошибку в Sentry
        SentryService.captureException(pollingError, {
          context: "startPaymentStatusPolling",
          bill_id: billId,
          attempts,
          originalError:
            error instanceof Error ? error.message : "Unknown error",
          interval,
        });

        stopPaymentStatusPolling(); // Останавливаем проверку
        onError(
          error instanceof Error
            ? error.message
            : "Ошибка проверки статуса оплаты",
        );
        return;
      }
    };

    // Запускаем первую проверку
    paymentStatusTimer = setTimeout(checkStatus, interval) as unknown as number;
  }

  return {
    tariffs,
    selectedTariff,
    paymentMethods,
    selectedPaymentMethod,
    isLoading,
    isLoadingMethods,
    isCreatingInvoice,
    isCheckingPayment,
    currentBillId,
    lastFailedBillRequest,
    error,
    errorStatus,
    // История транзакций
    billsHistory,
    autopayment,
    nextAutoPayDate,
    autopayAmount,
    isLoadingHistory,
    lastHistoryFetchTime,
    homeMinPrice,
    homeMinPriceFetchedAt,
    isLoadingHomeMinPrice,
    fetchHomeMinPrice,
    fetchTariffs,
    selectTariff,
    fetchPaymentMethods,
    selectPaymentMethod,
    createPaymentBill,
    retryLastFailedBill,
    checkPaymentStatus,
    startPaymentStatusPolling,
    stopPaymentStatusPolling,
    fetchBillsHistory,
    clearTariffs,
    clearPaymentMethods,
    clearBillsHistory,
    clearAll,
  };
});
